import type { ReactNode } from 'react';
import { ExternalLink, Loader2, RefreshCw } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import type { PreviewAccount } from '@/services/aiPreview';
import { COMPANY_KEY, displayUrl, type ScanEntry } from './shared';

interface ScanTabProps {
  account: PreviewAccount;
  entries: Record<string, ScanEntry>;
  scanning: Set<string>;
  checked: Set<string>;
  canRun: boolean;
  formatDate: (iso: string | null) => string | null;
  onToggle: (url: string) => void;
  onScan: (key: string) => void;
  onScanChecked: () => void;
}

export function ScanTab({ account, entries, scanning, checked, canRun, formatDate, onToggle, onScan, onScanChecked }: ScanTabProps) {
  const company = entries[COMPANY_KEY];
  const domain = account.company.domain;

  return (
    <div className="grid gap-5 lg:grid-cols-2 items-start">
      <Card className="gap-0 py-0">
        <SectionHeader
          title="Company website"
          subtitle={
            domain
              ? `From the signup email ${account.owner?.email ?? ''}`
              : 'The signup email has no company website (Gmail, an internet provider or a disposable address).'
          }
          action={
            <Button variant="outline" size="sm" disabled={!domain || !canRun || scanning.has(COMPANY_KEY)} onClick={() => onScan(COMPANY_KEY)}>
              {scanning.has(COMPANY_KEY) ? <Loader2 className="animate-spin" /> : <RefreshCw />}
              {company ? 'Scan again' : 'Scan'}
            </Button>
          }
        />
        <div className="px-5 py-4 flex flex-col gap-4">
          {scanning.has(COMPANY_KEY) && !company && <Scanning />}
          {!company && !scanning.has(COMPANY_KEY) && (
            <p className="text-sm text-muted-foreground">
              {domain ? `Not scanned yet. Scan ${domain} to see what the AI reads from it.` : 'Nothing to scan.'}
            </p>
          )}
          {company && <ScanDetails entry={company} formatDate={formatDate} showPages />}
        </div>
      </Card>

      <Card className="gap-0 py-0">
        <SectionHeader
          title="Embedded pages"
          subtitle="Every page this account's forms are on. Check the ones the preview uses."
          action={
            <Button variant="outline" size="sm" disabled={!canRun || checked.size === 0} onClick={onScanChecked} className="whitespace-nowrap">
              <RefreshCw />
              Scan checked
            </Button>
          }
        />
        <div className="px-5 py-4 flex flex-col gap-3">
          {account.pages.length === 0 && (
            <p className="text-sm text-muted-foreground">No form of this account is embedded on a page yet.</p>
          )}
          {account.pages.map((page) => {
            const entry = entries[page.url];
            const isChecked = checked.has(page.url);
            const busy = scanning.has(page.url);
            return (
              <article
                key={page.url}
                className={cn('rounded-lg border p-3.5 flex flex-col gap-2', isChecked && 'border-foreground')}
              >
                <div className="flex items-start justify-between gap-3">
                  <label className="flex items-start gap-2.5 font-mono text-[13px] cursor-pointer min-w-0">
                    <Checkbox checked={isChecked} onCheckedChange={() => onToggle(page.url)} className="mt-0.5" />
                    <span className="break-all">{displayUrl(page.url)}</span>
                  </label>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <a href={page.url} target="_blank" rel="noreferrer noopener" aria-label="Open the page" className="text-muted-foreground hover:text-foreground">
                      <ExternalLink className="size-3.5" />
                    </a>
                    <StatusBadge entry={entry} busy={busy} />
                  </div>
                </div>
                <div className="pl-[26px] flex flex-wrap items-center justify-between gap-2">
                  <span className="text-xs text-muted-foreground">
                    {page.subaccount
                      ? `${page.subaccount.isMain ? 'Main account' : 'Client subaccount'} · ${page.subaccount.name}`
                      : 'Its forms were deleted'}
                    {page.forms.length > 0 && ` · ${page.forms.join(', ')}`}
                  </span>
                  <Button variant="outline" size="sm" className="h-7 text-xs" disabled={!canRun || busy} onClick={() => onScan(page.url)}>
                    {busy && <Loader2 className="animate-spin" />}
                    {entry ? 'Scan again' : 'Scan this page'}
                  </Button>
                </div>
                {entry && (
                  <div className="pl-[26px]">
                    <ScanDetails entry={entry} formatDate={formatDate} compact />
                  </div>
                )}
                {isChecked && entry?.status !== 'done' && !busy && (
                  <p className="pl-[26px] text-xs text-amber-700 dark:text-amber-400">Checked, but the preview can only use it once a scan has read it.</p>
                )}
              </article>
            );
          })}
          <p className="text-xs text-muted-foreground leading-relaxed">
            The newest checked page is the latest landing page in the GHL preview and the email. The live job scans main account pages only; here you can scan any of them.
          </p>
        </div>
      </Card>
    </div>
  );
}

function SectionHeader({ title, subtitle, action }: { title: string; subtitle: string; action?: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 px-5 py-4 border-b">
      <div className="flex flex-col gap-0.5 min-w-0">
        <h2 className="text-base font-semibold">{title}</h2>
        <span className="text-[13px] text-muted-foreground">{subtitle}</span>
      </div>
      {action}
    </div>
  );
}

function Scanning() {
  return (
    <div role="status" className="flex items-center gap-2 text-sm text-muted-foreground">
      <Loader2 className="size-4 animate-spin" />
      Reading the page and asking the AI to describe it. This can take a minute.
    </div>
  );
}

function StatusBadge({ entry, busy }: { entry: ScanEntry | undefined; busy: boolean }) {
  if (busy) return <Badge tone="info">Scanning</Badge>;
  if (!entry) return <Badge tone="neutral">Not scanned</Badge>;
  if (entry.status === 'done') return <Badge tone="success">Scanned</Badge>;
  if (entry.status === 'failed') return <Badge tone="warning">Couldn't read</Badge>;
  return <Badge tone="neutral">Scan running</Badge>;
}

function ScanDetails({
  entry,
  formatDate,
  showPages = false,
  compact = false,
}: {
  entry: ScanEntry;
  formatDate: (iso: string | null) => string | null;
  showPages?: boolean;
  compact?: boolean;
}) {
  const s = entry.summary;
  const when = formatDate(entry.scannedAt);
  return (
    <div className="flex flex-col gap-3">
      <span className="text-xs text-muted-foreground">
        {entry.source === 'preview'
          ? `Scanned here ${when ? `on ${when}` : ''}, not stored`
          : entry.status === 'pending'
            ? 'The AI pilot is scanning it now'
            : `Saved by the AI pilot${when ? ` on ${when}` : ''}`}
      </span>

      {showPages && entry.pages.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">Pages read</span>
          {entry.pages.map((p, i) => (
            <div key={`${p.url}-${i}`} className="flex justify-between gap-3 text-[13px] px-2.5 py-2 bg-muted/50 rounded-md">
              <span className="font-mono break-all">{displayUrl(p.url)}</span>
              {p.ok ? (
                <span className="text-emerald-700 dark:text-emerald-400 shrink-0">Read · {p.chars.toLocaleString('en-US')} chars</span>
              ) : (
                <span className="text-red-700 dark:text-red-400 shrink-0 text-right">{p.error || "Couldn't fetch"}</span>
              )}
            </div>
          ))}
        </div>
      )}

      {entry.status === 'failed' && <p className="text-[13px] text-muted-foreground leading-relaxed">{entry.error}</p>}

      {s && compact && (
        <>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[13px] text-foreground/80">
            {[s.businessName, s.niche, s.location].filter(Boolean).map((v) => (
              <span key={v}>{v}</span>
            ))}
          </div>
          <p className="text-[13px] leading-relaxed">{s.summary}</p>
        </>
      )}

      {s && !compact && (
        <>
          <dl className="grid grid-cols-[110px_minmax(0,1fr)] gap-x-4 gap-y-2.5 text-[13px]">
            {(
              [
                ['Business', s.businessName],
                ['Type', s.businessType],
                ['Niche', s.niche],
                ['Main offer', s.offer],
                ['Sells to', s.targetCustomer],
                ['Location', s.location],
              ] as const
            ).map(([label, value]) => (
              <div key={label} className="contents">
                <dt className="text-muted-foreground">{label}</dt>
                <dd className={cn(!value && 'text-muted-foreground')}>{value || 'Not on the site'}</dd>
              </div>
            ))}
          </dl>
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-muted-foreground">AI summary</span>
            <p className="text-[13px] leading-relaxed">{s.summary}</p>
          </div>
        </>
      )}
    </div>
  );
}
